import { hasPrivateAnswerMarker, isSupportedPrivateText, privateTextWireType, collectPrivateAnswerIds } from "./privateAnswerSchema";
import definitionDefaults from "../data/propertyPanelDefaultBaselines.json";
import { isPrivateNotesActivity, privateNotesContext } from "./privateNotesScope";
import { validateInvoiceNotesReceipt } from "./privateInvoiceNotesProtocol";
export { PRIVATE_NOTES_ORG, isPrivateNotesActivity, privateNotesContext } from "./privateNotesScope";
export const isPrivateNotesId = id => typeof id === "string" && /^labelInternalNotes(?:_[A-Za-z0-9_-]+)?$/.test(id);

// Mapped activities (enrolled ids, or every JAB activity with the org-wide switch)
// cross this boundary. The legacy job-detail "Parts" value is kept as it is: it is
// read-only "Internal Notes (legacy)" for staff until they retire it, and the
// customer/print projection removes it.
export function sanitizeJabPublicPayload(value, orgId, activity = value) {
  if (Array.isArray(value) && activity === value) {
    const projected = value.map(entry => sanitizeJabPublicPayload(entry, orgId));
    return projected.every((entry, index) => entry === value[index]) ? value : projected;
  }
  const privateIds = collectPrivateAnswerIds(activity);
  collectPrivateAnswerIds(value, privateIds);
  const legacy = isPrivateNotesActivity(activity, orgId);
  return legacy || privateIds.size ? sanitizePrivatePayload(value, "", privateIds, legacy) : value;
}
function sanitizePrivatePayload(value, parentKey = "", privateIds = new Set(), legacy = true) {
  const privateId = id => privateIds.has(id) || (typeof id === "string" && id.startsWith("field:") && privateIds.has(id.slice(6))) || (legacy && isPrivateNotesId(id));
  if (Array.isArray(value)) return value.map(v => sanitizePrivatePayload(v, parentKey, privateIds, legacy)).filter(v => v !== undefined);
  if (!value || typeof value !== "object" || value instanceof Date) return value;
  if (hasPrivateAnswerMarker(value) || privateId(value.id)) {
    if (isSupportedPrivateText(value)) {
      const stub = { id: value.id, type: value.type, label: "Internal Notes", label1: "Internal Notes", privateNotes: true };
      if (hasPrivateAnswerMarker(value)) {
        stub.saveAsPrivate = true; stub.type = privateTextWireType(value);
        // Prompts are public authored metadata; answers and computed defaults are not.
        if (!legacy || !isPrivateNotesId(value.id)) {
          stub.label = typeof value.label === "string" ? value.label : "Private answer";
          stub.label1 = typeof value.label1 === "string" ? value.label1 : stub.label;
        }
        if (typeof value.required === "boolean") stub.required = value.required;
      }
      // Retain only structural navigation/completion metadata, never labels,
      // defaults, answers, validations, or copied content from the source.
      if (typeof value.guid === "string" && /^[A-Za-z0-9_-]{1,128}$/.test(value.guid)) stub.guid = value.guid;
      if (typeof value.privateAnswerDefinitionGuid === "string" && /^[A-Za-z0-9_-]{1,121}$/.test(value.privateAnswerDefinitionGuid)) stub.privateAnswerDefinitionGuid = value.privateAnswerDefinitionGuid;
      ["visible", "enabled", "checked", "required", "mandatory", "notApplicable", "ignoreCompletion", "textInputKeyboardReturnKeySubmitsItem"].forEach(key => { if (typeof value[key] === "boolean") stub[key] = value[key]; });
      if (Number.isInteger(value.textInputMaxLength) && value.textInputMaxLength > 0) stub.textInputMaxLength = value.textInputMaxLength;
      return stub;
    }
    return undefined; // Private source answer in an audit/history entry.
  }
  if ([value.itemId, value.nodeId, value.sourceId, value.variableName, value.itemGuid, value.guid].some(privateId)) return undefined;
  return Object.keys(value).reduce((out, key) => {
    if (privateId(key) || ["privateInternalNotes", "privateNotesRecord"].includes(key) || (legacy && key === "internalNotesSource")) return out;
    let entry = value[key];
    // JSON snapshots may be nested as strings by legacy audit/save paths.
    if (typeof entry === "string" && /^[[{]/.test(entry.trim())) {
      try { entry = JSON.stringify(sanitizePrivatePayload(JSON.parse(entry), key, privateIds, legacy)); } catch (_) { /* ordinary text */ }
    } else entry = sanitizePrivatePayload(entry, key, privateIds, legacy);
    if (entry !== undefined) out[key] = entry;
    return out;
  }, {});
}

export function serializeJabPublicPayload(value, orgId, activity) {
  const parsed = typeof value === "string" ? JSON.parse(value) : value;
  const sanitized = sanitizeJabPublicPayload(parsed, orgId, activity || parsed);
  return sanitized === parsed && typeof value === "string" ? value : JSON.stringify(sanitized);
}

// Definition files have an explicit trusted-writer purpose. Preserve the
// authored schema only in the activity hierarchy, never in instance/audit copies.
const PRIVATE_DEFINITION_KEYS = new Set([
  ...Object.keys(definitionDefaults.itemsByType.itemTextInputMultiline),
  "saveAsPrivate", "guid", "type", "checked", "notApplicable", "showModeTextAlign",
  "useCustomShowModeStyling", "commentsVisibility", "authorProperties",
  "backgroundAudioDuckingDuck", "backgroundAudioDuckingDuration", "backgroundAudioDuckingVolume",
  "fullscreenTransition", "fullscreenTransitionIn", "fullscreenTransitionOut", "fullscreenTransitionDelay",
  "isSearchable", "useAsSessionName", "conditionalVisible", "calculatedDefaultValue",
  "valueTrigger", "startNamedTimers", "stopNamedTimers", "textInputCustomMask2"
]);
const PRIVATE_DEFINITION_DEFAULTS = new Set(["textInputDefaultValue", "calculatedDefaultValue"]);

export function sanitizeJabActivityDefinition(value, orgId) {
  if (!isPrivateNotesActivity(value, orgId) && collectPrivateAnswerIds(value).size === 0) return value;
  // A history/instance artifact cannot opt into definition treatment.
  if (!value || value.instanceId || (Array.isArray(value.history) && value.history.length)) {
    return sanitizeJabPublicPayload(value, orgId);
  }
  const privateIds = collectPrivateAnswerIds(value);
  const legacy = isPrivateNotesActivity(value, orgId);
  const visitDefinition = node => {
    if (!node || typeof node !== "object") return node;
    if ((hasPrivateAnswerMarker(node) || (legacy && isPrivateNotesId(node.id))) && isSupportedPrivateText(node)) {
      return Object.keys(node).reduce((out, key) => {
        if (!PRIVATE_DEFINITION_KEYS.has(key)) return out;
        // Never publish a private answer as an authored default, including
        // copies pasted from an instance. The private editor supplies values.
        if (PRIVATE_DEFINITION_DEFAULTS.has(key)) out[key] = "";
        else if (key === "type" && hasPrivateAnswerMarker(node)) out[key] = privateTextWireType(node);
        else {
          const sanitized = sanitizePrivatePayload(node[key], key, privateIds, legacy);
          if (sanitized !== undefined) out[key] = sanitized;
        }
        return out;
      }, {});
    }
    const clean = sanitizePrivatePayload(node, "", privateIds, legacy);
    if (clean && Array.isArray(node.children)) clean.children = node.children.map(visitDefinition).filter(child => child !== undefined);
    return clean;
  };
  return visitDefinition(value);
}

export function serializeJabFilePayload(file, orgId) {
  const value = file.fileContent;
  // Only the authored source/published-definition writer sets this marker;
  // flags embedded inside untrusted activity content have no effect.
  if (file.activityDefinition !== true) return serializeJabPublicPayload(value, orgId);
  const parsed = typeof value === "string" ? JSON.parse(value) : value;
  if (!parsed || file.fileName !== `${parsed.id}.json`) return serializeJabPublicPayload(value, orgId);
  const clean = sanitizeJabActivityDefinition(parsed, orgId);
  return clean === parsed && typeof value === "string" ? value : JSON.stringify(clean);
}

export function privateNotesIdentity(activity, orgId, estimateId = "original") {
  const context = privateNotesContext(activity, orgId);
  if (!context) return null;
  const { protocolVersion, ...identity } = context;
  return { ...identity, estimateId: estimateId || "original" };
}

// Same-page invalidation only: subscribers receive no private text or receipt.
// Separate modal clients reread through their own authenticated transport.
const privateNotesSubscribers = new Map();
const privateNotesUpdateKey = (endpoint, identity) => {
  const fields = [endpoint?.replace(/\/$/, ""), identity?.orgId, identity?.activityId, identity?.instanceId, identity?.estimateId || "original"];
  return fields.every(value => typeof value === "string" && value.length > 0) ? JSON.stringify(fields) : null;
};

export function createPrivateNotesClient({ endpoint, getToken, refreshToken, fetchImpl = (...args) => fetch(...args), timeoutMs = 15000 }) {
  const validEndpoint = typeof endpoint === "string" && /^https:\/\/[^/?#]+(?:\/[^?#]*)?$/.test(endpoint);
  const localEndpoint = process.env.NODE_ENV !== "production" && /^http:\/\/(localhost|127\.0\.0\.1):[0-9]+$/.test(endpoint || "");
  const fail = (code, message) => Object.assign(new Error(message), { code });
  let refreshing;
  const renew = async () => {
    if (!refreshToken) throw fail("NOT_AUTHENTICATED", "Your session expired. Sign in again; your draft is still here.");
    if (!refreshing) refreshing = Promise.resolve().then(refreshToken).finally(() => { refreshing = null; });
    let timer;
    try { await Promise.race([refreshing, new Promise((_, reject) => { timer = setTimeout(() => reject(new Error()), timeoutMs); })]); }
    catch (_) { throw fail("NOT_AUTHENTICATED", "Your session could not be refreshed. Sign in again; your draft is still here."); }
    finally { clearTimeout(timer); }
  };
  async function request(path, body, refreshed = false) {
    if (!validEndpoint && !localEndpoint) throw fail("UNAVAILABLE", "Private answers are not configured.");
    const token = await getToken();
    if (!token) {
      if (!refreshed) { await renew(); return request(path, body, true); }
      throw fail("NOT_AUTHENTICATED", "Sign in again to save your private answer. Your draft is still here.");
    }
    const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
    let timer, response;
    try {
      response = await Promise.race([
        fetchImpl(endpoint.replace(/\/$/, "") + "/internal-notes/" + path, { method: "POST", headers: { Authorization: "Bearer " + token.replace(/^Bearer /i, ""), "Content-Type": "application/json" }, body: JSON.stringify(body), cache: "no-store", credentials: "omit", redirect: "error", referrerPolicy: "no-referrer", ...(controller ? { signal: controller.signal } : {}) }),
        new Promise((_, reject) => { timer = setTimeout(() => { if (controller) controller.abort(); reject(new Error()); }, timeoutMs); })
      ]);
    } catch (_) { throw fail("UNAVAILABLE", "The private answer could not be saved or loaded. Your draft is still here. Try again."); }
    finally { clearTimeout(timer); }
    if (response.status === 401) {
      if (!refreshed) { await renew(); return request(path, body, true); }
      throw fail("NOT_AUTHENTICATED", "Your session expired. Sign in again; your draft is still here.");
    }
    if (!response.ok) {
      if (response.status === 409) throw fail("REVISION_CONFLICT", "This answer changed elsewhere. Review the saved answer; your draft is still here.");
      if (response.status === 403) throw fail("NOT_AUTHORIZED", "You do not have access to this private answer.");
      if (response.status === 404) throw fail("JOB_NOT_AVAILABLE", "This job is not ready for private answers yet. Your draft is still here; try again.");
      if (response.status === 400) throw fail("INVALID_UPDATE", "This private answer could not be accepted. Check its length and try again.");
      if (response.status === 423) throw fail("ACTIVITY_LOCKED", "This activity is locked. Your draft is still here; it has not been saved.");
      throw fail("UNAVAILABLE", "Private answers are temporarily unavailable. Your draft is still here.");
    }
    let result;
    try {
      result = await Promise.race([response.json(), new Promise((_, reject) => { timer = setTimeout(() => { if (controller) controller.abort(); reject(new Error()); }, timeoutMs); })]);
    } catch (_) { throw fail("UNAVAILABLE", "The private save receipt could not be read. Your draft is still here."); }
    finally { clearTimeout(timer); }
    if (body.purpose === "staffInvoiceNotes") return validateInvoiceNotesReceipt(result);
    if (!result || typeof result.text !== "string" || !Number.isSafeInteger(result.revision) || result.revision < 0 || result.canRead !== true || typeof result.canEdit !== "boolean" || !result.sources || Object.values(result.sources).some(value => typeof value !== "string")) throw fail("UNAVAILABLE", "The private save receipt could not be verified. Your draft is still here.");
    return result;
  }
  return {
    readInvoiceNotes: identity => request("read", { ...identity, purpose: "staffInvoiceNotes" }),
    writeInvoiceNote: async (identity, update) => {
      const result = await request("write", { ...identity, ...update, purpose: "staffInvoiceNotes" });
      const listeners = privateNotesSubscribers.get(privateNotesUpdateKey(endpoint, identity));
      if (listeners) [...listeners].forEach(listener => { try { listener(); } catch (_) { /* confirmed save */ } });
      return result;
    },
    read: identity => request("read", identity),
    readCorrections: async identity => validateCorrectionReceipt(await request("read", { ...identity, adminCorrection: true })),
    writeCorrection: async (identity, update) => validateCorrectionReceipt(await request("write", { ...identity, ...update, operation: "source", adminCorrection: true })),
    subscribe: (identity, listener) => {
      const key = privateNotesUpdateKey(endpoint, identity);
      if (!key) return () => {};
      const listeners = privateNotesSubscribers.get(key) || new Set();
      listeners.add(listener); privateNotesSubscribers.set(key, listeners);
      return () => {
        listeners.delete(listener);
        if (!listeners.size) privateNotesSubscribers.delete(key);
      };
    },
    write: async (identity, update) => {
      const result = await request("write", { ...identity, ...update });
      const listeners = privateNotesSubscribers.get(privateNotesUpdateKey(endpoint, identity));
      if (listeners) [...listeners].forEach(listener => {
        try { listener(); } catch (_) { /* A view refresh cannot invalidate a confirmed save. */ }
      });
      return result;
    }
  };
}

// The admin panel must not infer permission from a client role or an older
// service response. Every displayed source has a server-resolved descriptor.
export function validateCorrectionReceipt(result) {
  const fields = result.correctionFields;
  if (result.adminCorrectionProtocol !== 1 || typeof result.canEditSources !== "boolean" || !Array.isArray(fields) || fields.length > 100
    || fields.some(field => !field || !/^(?:field:[A-Za-z0-9_-]{1,121}|labelInternalNotes(?:_[A-Za-z0-9_-]+)?)$/.test(field.sourceId)
      || !/^[A-Za-z0-9_-]{1,121}$/.test(field.definitionGuid) || typeof field.label !== "string" || typeof field.multiline !== "boolean"
      || !Number.isInteger(field.maxLength) || field.maxLength < 0 || field.maxLength > 16000 || typeof result.sources[field.sourceId] !== "string")
    || new Set(fields.map(field => field.sourceId)).size !== fields.length || Object.keys(result.sources).length !== fields.length) {
    throw Object.assign(new Error("Private answer corrections are unavailable. Please try again later."), { code: "UNAVAILABLE" });
  }
  return result;
}
